import React, { useState, useEffect } from "react";
import { useNavigate, Navigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { ShieldCheck, Stethoscope } from "lucide-react";
import BrandLogo from "../components/BrandLogo";

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [expiredBanner, setExpiredBanner] = useState(searchParams.get("reason") === "expired");

  // If we got bounced here by the 401 interceptor, show a friendly toast once
  // and clear the query param so a refresh doesn't re-fire it.
  useEffect(() => {
    if (searchParams.get("reason") === "expired") {
      toast.info("Session expired. Please log in again.");
      setExpiredBanner(true);
      // Strip the query param without reloading
      const next = new URLSearchParams(searchParams);
      next.delete("reason");
      setSearchParams(next, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (user) {
    if (user.must_change_password) return <Navigate to="/change-password" replace />;
    return <Navigate to={user.access_role === "Staff" ? "/me" : "/dashboard"} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      if (u.must_change_password) {
        toast.info("Please set a permanent password to continue.");
        navigate("/change-password", { replace: true });
        return;
      }
      toast.success(`Welcome, ${u.full_name}`);
      navigate(u.access_role === "Staff" ? "/me" : "/dashboard");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403 && detail) toast.error(detail);
      else if (status === 401) toast.error("Invalid email or password.");
      else toast.error(detail || "Sign-in failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-col justify-between flex-1 bg-gradient-to-br from-sky-900 via-sky-800 to-emerald-700 text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage:
            "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.4) 0, transparent 40%), radial-gradient(circle at 80% 60%, rgba(255,255,255,0.3) 0, transparent 35%)",
        }} />
        <div className="relative">
          <div className="flex items-center gap-3">
            <BrandLogo size={44} rounded="rounded-xl" />
            <div>
              <p className="font-heading text-lg font-bold tracking-tight">NestVillage</p>
              <p className="text-xs text-white/80">Respect · Dignity · Empathy</p>
            </div>
          </div>
          <div className="mt-24 max-w-md">
            <h1 className="font-heading text-4xl font-bold leading-tight">Caring for those who care for our community.</h1>
            <p className="mt-5 text-white/85 leading-relaxed">Multi-home performance tracking, monthly KPI scoring, QA audits, and leaderboards — all in one secure place.</p>
          </div>
        </div>
        <div className="relative grid grid-cols-2 gap-4 max-w-md">
          <div className="rounded-xl bg-white/10 backdrop-blur border border-white/15 p-4">
            <Stethoscope size={18} />
            <p className="mt-2 text-sm font-semibold">6 Group Homes</p>
            <p className="text-xs text-white/75">Isolated data per home</p>
          </div>
          <div className="rounded-xl bg-white/10 backdrop-blur border border-white/15 p-4">
            <ShieldCheck size={18} />
            <p className="mt-2 text-sm font-semibold">HIPAA-aware</p>
            <p className="text-xs text-white/75">Role-based privacy</p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 flex items-center gap-2">
            <BrandLogo size={40} />
            <p className="font-heading text-lg font-bold">NestVillage Care Metrics</p>
          </div>
          <h2 className="text-h1">Sign in</h2>
          <p className="text-sm text-slate-500 mt-1.5">Enter your email and password to continue.</p>

          {expiredBanner && (
            <div
              className="mt-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2"
              data-testid="session-expired-banner"
              role="status"
            >
              <span className="font-semibold">Session expired.</span>
              <span>Please log in again.</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="text-small-caps">Email</label>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/40 focus:border-sky-400"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="text-small-caps">Password</label>
              <input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/40 focus:border-sky-400"
                data-testid="login-password-input"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full px-4 py-2.5 rounded-lg bg-sky-800 hover:bg-sky-900 text-white text-sm font-semibold transition-colors disabled:opacity-60"
              data-testid="login-submit-btn"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
            <div className="text-center">
              <Link to="/forgot-password" className="text-xs font-medium text-sky-700 hover:text-sky-900 hover:underline" data-testid="forgot-password-link">
                Forgot your password?
              </Link>
            </div>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400 font-medium">OR</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <button
            type="button"
            onClick={handleGoogle}
            className="w-full px-4 py-2.5 rounded-lg bg-white border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
            data-testid="login-google-btn"
          >
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.1A6.97 6.97 0 0 1 5.5 12c0-.73.13-1.45.34-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.83z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Continue with Google
          </button>

          <div className="mt-8 rounded-lg border border-slate-200 bg-white p-3.5 text-xs text-slate-500">
            <p className="font-semibold text-slate-700 mb-0.5">Default password</p>
            All imported users have the default password <code className="bg-slate-100 px-1.5 py-0.5 rounded">123455</code>. Change it after first login.
          </div>
        </div>
      </div>
    </div>
  );
}
